import flatten from 'lodash/flatten';

import { PracticeApi } from '@api/PracticeApi';
import {
    OrganizationCreateForm,
    OrganizationStatus,
    OrganizationUpdateForm,
    OrganizationType,
    OrganizationData,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { OrganizationApi } from '@organization/api/OrganizationApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

class OrganizationService {
    private isPracticeLoaded = false;
    private isOrganizationsLoaded = false;
    private isClinicsLoaded = false;
    private isAllOrganizationsLoaded = false;

    public async load(): Promise<void> {
        const organizationTypes = [
            OrganizationType.ExternalClinic,
            OrganizationType.Laboratory,
            OrganizationType.Organization,
            OrganizationType.Clinic,
            OrganizationType.InsuranceCompany,
            OrganizationType.Assistance,
            OrganizationType.Charity,
        ];

        if (!this.isOrganizationsLoaded) {
            const { organizations } = await OrganizationApi.getList({
                types: organizationTypes.join(',') as OrganizationType,
            });

            store.organizationList.add(organizations);
            this.isOrganizationsLoaded = true;
            this.isClinicsLoaded = true;
        }
    }

    public async loadClinics(): Promise<void> {
        const organizationTypes = [OrganizationType.Clinic];

        if (!this.isClinicsLoaded) {
            const { organizations } = await OrganizationApi.getList({
                types: organizationTypes.join(',') as OrganizationType,
            });

            store.organizationList.add(organizations);
            this.isClinicsLoaded = true;
        }
    }

    public async loadAll(): Promise<void> {
        if (!this.isAllOrganizationsLoaded) {
            const { organizations } = await OrganizationApi.getList({});

            store.organizationList.set(organizations);
            this.isAllOrganizationsLoaded = true;
            this.isOrganizationsLoaded = true;
        }
    }

    public async loadPractices(): Promise<void> {
        const { practiceList } = store.organizationList;
        if (!practiceList.isDataSet && !this.isPracticeLoaded) {
            const practiceContact = await PracticeApi.getPracticeData();

            practiceList.set(practiceContact);
            this.isPracticeLoaded = true;
        }
    }

    public async loadByIds(ids: string[]): Promise<OrganizationData[]> {
        const organizationList = store.organizationList;

        const newIds = getUniqNewIds(organizationList.ids, ids);

        if (newIds.length > 0) {
            const chunkIds = getUniqChunkedIds(newIds);

            const organizationsParams = await Promise.all(
                chunkIds.map(ids => OrganizationApi.getList({ ids: ids.join(',') })),
            );

            organizationsParams.forEach(({ organizations, count }) => {
                organizationList.add(organizations);
            });

            return flatten(organizationsParams.map(({ organizations }) => organizations));
        }

        return [];
    }

    public async create(params: OrganizationCreateForm): Promise<void> {
        const organizationList = store.organizationList;

        await OrganizationApi.create(params);
        const organization = await OrganizationApi.get(params.id);

        organizationList.add([organization]);
    }

    public async update(id: string, updateParams: OrganizationUpdateForm): Promise<void> {
        const organizationList = store.organizationList;

        const current = organizationList.get(id);

        if (updateParams && Object.keys(updateParams).length > 0) {
            await OrganizationApi.update(id, updateParams);
            organizationList.update(id, { ...current, ...updateParams });
        }
    }

    public async updateStatus(id: string, status: OrganizationStatus): Promise<void> {
        const organizationList = store.organizationList;

        await OrganizationApi.updateStatus(id, status);

        const current = organizationList.get(id);

        organizationList.update(id, { ...current, status });
    }

    public async loadOrganizationPractices(organizationId: string): Promise<void> {
        const organizationList = store.organizationList;
        if (!organizationList.isOrganizationPracticesSet(organizationId)) {
            organizationList.setOrganizationPractices(organizationId, []);
            const [practices] = await Promise.all([PracticeApi.getOrganizationWalkInPractices(organizationId)]);

            organizationList.setOrganizationPractices(organizationId, practices);
        }
    }
}

export const organizationService = new OrganizationService();
